// src/services/sourcexWorkflow.js
//
// SourceX — "where in the three steps is this study, and where should this
// person be?"
//
// Building a study's source documents is three ordered steps followed by a
// destination:
//
//   1. Upload Protocol        /sourcex/protocols/:studyId
//   2. Upload Blank eCRF      /sourcex/ecrf/:studyId
//   3. Map & Create Documents /sourcex/join/:studyId
//   →  Built source documents /sourcex/template/:studyId__assembled
//
// Every one of those pages is linkable from the home page at any time, so
// landing on the wrong one is the normal case rather than the exceptional one.
// Each page therefore has to answer the same question — what has this study
// actually finished, and what is the furthest real thing to offer? — and they
// must all answer it identically, or step 1 and step 2 will point a confused
// coordinator in two different directions.
//
// PURE. No Firebase, no React: it takes the documents a page has already
// loaded and returns a label and a route. That is what lets the ordering be
// tested against every combination rather than clicked through by hand.

/**
 * Statuses that mean an uploaded file's extraction has produced something a
 * human can look at. Shared by protocols and eCRFs, which use the same words.
 */
const REVIEWABLE = new Set(["needs_review", "approved"]);

/** Has this upload got far enough that opening its review page shows anything?
 *
 * While a file is `uploaded` or `processing` the extraction document does not
 * exist yet, so its review page renders an empty shell. "Go and review it" is
 * only ever offered once this is true.
 *
 * @param {object} [item] a protocol or eCRF document
 * @returns {boolean}
 */
export const isReviewable = (item) => REVIEWABLE.has(item?.status);

/**
 * Is this upload still WAITING on a human?
 *
 * Narrower than `isReviewable`, and the distinction matters: an approved
 * extraction can still be opened and looked at, but it is finished work. Only
 * `needs_review` should pull somebody backwards to a step they have already
 * passed — "Review this protocol" on a protocol that was signed off last week
 * sends them to re-do nothing.
 *
 * @param {object} [item] a protocol or eCRF document
 * @returns {boolean}
 */
export const needsReview = (item) => item?.status === "needs_review";

/** Signed off by a human. */
export const isApproved = (item) => item?.status === "approved";

/**
 * When the approval gate below started to apply.
 *
 * The gate is a NEW RULE, and a new rule that reaches backwards blocks studies
 * that are already halfway through — a coordinator who uploaded a protocol last
 * week and moved on to step 2 would come back to find step 2 shut, with nothing
 * having changed about their study. So protocols uploaded before this instant
 * are exempt: the rule applies to work started after it, and existing studies
 * carry on exactly as they did.
 *
 * This is deliberately a fixed date rather than a rolling window — it marks the
 * one moment the rule changed, and it must not move.
 */
const APPROVAL_GATE_FROM = Date.UTC(2026, 8, 2); // 2 September 2026

/** Milliseconds from a Firestore Timestamp, a Date, or a number. */
const whenMs = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Was this protocol uploaded before the gate existed?
 *
 * A MISSING timestamp is not treated as old. Every protocol document has
 * carried `uploadedAt` since the collection existed — `uploadAndCreateProtocol`
 * writes it in the same call that creates the document — so an absent value
 * means the server timestamp has not landed yet, which makes it seconds old,
 * not months. Reading it as legacy would exempt exactly the new uploads the
 * gate is for.
 */
const predatesApprovalGate = (protocol) => {
  const ms = whenMs(protocol?.uploadedAt) ?? whenMs(protocol?.createdAt);
  return ms !== null && ms < APPROVAL_GATE_FROM;
};

/**
 * Is step 1 FINISHED — a protocol uploaded *and* its schedule of assessments
 * approved?
 *
 * This used to be existence alone, on the reasoning that the mapping step falls
 * back to the working extraction when there is no approved one, so requiring
 * approval would block a path the server accepts. The server does still accept
 * it; the point is that it shouldn't be reached. An unapproved extraction is
 * the AI's unreviewed reading of the protocol, and everything downstream —
 * the join, and every visit's source document built from it — inherits whatever
 * it got wrong. Letting step 2 start on top of that buys nothing except the
 * chance to discover the errors after they have been propagated.
 *
 * So approval is the gate, in ONE place, because step 2 and step 3 both ask and
 * two pages disagreeing about whether a study may proceed is worse than either
 * answer.
 *
 * Applies to protocols uploaded from APPROVAL_GATE_FROM onward only. A study
 * that was already under way keeps working exactly as it did — see
 * predatesApprovalGate.
 *
 * A `null` list means "still loading" and is NOT finished — but every caller
 * holds the gate behind its own `loading` check, so that never shows as a
 * refusal.
 *
 * @param {Array<object>|null} [protocols]
 * @returns {boolean}
 */
export const protocolApproved = (protocols) =>
  (protocols || []).some((p) => isApproved(p) || predatesApprovalGate(p));

/**
 * The furthest step this study has actually reached, as something to click.
 *
 * Ordered by how far the work has got, so the answer is always the most
 * advanced thing that is REAL:
 *
 *   built documents  >  a protocol waiting to be reviewed  >  both uploaded, go
 *   and map them  >  "you haven't done step 2 yet"
 *
 * A protocol waiting for review outranks an uploaded eCRF on purpose: step 3
 * consumes the APPROVED protocol extraction, so sending someone to the join
 * screen while step 1 is still unreviewed sends them somewhere they cannot
 * finish. The eCRF has no equivalent gate, so an uploaded one goes straight to
 * step 3 whatever its extraction thinks of itself.
 *
 * The final fallback assumes the protocol exists, because every caller is a
 * page that only offers this once something has been uploaded. A study with
 * nothing at all does not need pointing anywhere — it needs the one button its
 * page already shows.
 *
 * `currentPath` drops any candidate that is the page ASKING. Two of these
 * routes are a step page's own address — the eCRF page both reviews an eCRF and
 * is step 2 — and "go to the page you are already on" is the one answer that
 * cannot help anybody. Whatever is left below it is offered instead, and if
 * nothing is, the SourceX home page always is.
 *
 * @param {object} args
 * @param {string} args.studyId
 * @param {Array<object>} [args.protocols]
 * @param {Array<object>} [args.ecrfs]
 * @param {boolean} [args.hasAssembled]  a `{studyId}__assembled` template exists
 * @param {string} [args.currentPath]  the route of the page asking
 * @returns {{label: string, hint: string, to: string}}
 */
export const nextWorkflowStep = ({
  studyId,
  protocols = [],
  ecrfs = [],
  hasAssembled = false,
  currentPath = "",
  // Protocol versions (optional; absent = exactly as before): `isLocked(p)` is
  // an older version that mustn't be reviewed, and `readFirst` is the current
  // version's protocol waiting to be read ({ version }).
  isLocked = null,
  readFirst = null,
}) => {
  // `needsReview`, never `isReviewable`: an APPROVED extraction is finished
  // work, and offering "review it" on something signed off last week sends
  // somebody backwards to re-do nothing.
  //
  // Only the PROTOCOL earns a review stop. Step 3 consumes the approved
  // protocol extraction, so an unreviewed protocol is a genuine blocker. An
  // eCRF has no such gate — once it is uploaded the mapping step can run — so
  // parking a coordinator on "review this eCRF" only delays the one thing they
  // came to do.
  const protocol = (protocols || []).find((p) => needsReview(p) && !(isLocked && isLocked(p)));

  const candidates = [
    hasAssembled && {
      label: "Open built source documents",
      hint: "Already built — this is where you want to be.",
      to: `/sourcex/template/${studyId}__assembled`,
    },
    readFirst && {
      label: readFirst.version ? `Read Version ${readFirst.version} of the protocol` : "Read the new protocol",
      hint: "A new version was uploaded and hasn't been read yet.",
      to: `/sourcex/protocols/${studyId}`,
    },
    protocol && {
      label: "Review this protocol",
      hint: "Its schedule of assessments is extracted and waiting.",
      to: `/sourcex/protocols/${studyId}/${protocol.id}`,
    },
    (ecrfs || []).length > 0 && {
      label: "Step 3 · Map & Create Documents",
      hint: "The protocol and the eCRF are both uploaded.",
      to: `/sourcex/join/${studyId}`,
    },
    {
      label: "Step 2 · Upload Blank eCRF",
      hint: "The protocol is uploaded — the eCRF is next.",
      to: `/sourcex/ecrf/${studyId}`,
    },
  ];

  return (
    candidates.find((c) => c && c.to !== currentPath) || {
      label: "Back to SourceX",
      hint: "Pick a study and start from the top.",
      to: "/sourcex",
    }
  );
};
